import React, { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useParams } from "react-router-dom";
import { OverlayScrollbars } from "overlayscrollbars";
import "overlayscrollbars/overlayscrollbars.css";
import "@rollout/link-react/style.css";
import "./index.css";
import "./App.css";

// Suppress third-party tracking script errors (Facebook Pixel via HubSpot)
// These errors occur in Firefox due to Enhanced Tracking Protection blocking storage access
window.addEventListener('error', (event) => {
  if (event.message?.includes('operation is insecure') ||
      event.filename?.includes('fbevents') ||
      event.filename?.includes('facebook')) {
    event.preventDefault();
    return true;
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('operation is insecure')) {
    event.preventDefault();
    return true;
  }
});

import { Toaster } from "./components/shadcn/Toaster";
import NavigationProvider from "./providers/NavigationProvider";
import SplashScreen from "./components/SplashScreen";
import ErrorBoundary from "./components/utils/error/ErrorBoundary";
import RouterErrorPage from "./components/common/RouterErrorPage";
import { AuthProvider } from "./components/utils/auth/assist/authContext";
import { SendCardsProvider } from "./components/sendCards";
import { AreaProProvider } from "./components/areaPro";
import ImportedListingsPage from "./routes/ImportedListingsPage";
import SearchExpiredsPage from "./routes/SearchExpiredsPage";
import NeighborhoodSearchPage from "./routes/NeighborhoodSearchPage";
const Login = lazy(() => import("./routes/Login"));
const ForgotPassword = lazy(() => import("./routes/ForgotPassword"));
const Settings = lazy(() => import("./routes/Settings"));
const OldExpired = lazy(() => import("./routes/OldExpired"));
const Fsbo = lazy(() => import("./routes/SearchFsbosPage"));
const Preforeclosures = lazy(() => import("./routes/PreforeclosuresLeads"));
const Distressed = lazy(() => import("./routes/Distressed"));
const Scripts = lazy(() => import("./routes/Scripts"));
const TutorialsTraining = lazy(() => import("./routes/TutorialsTraining"));
const ResetPassword = lazy(() => import("./routes/ResetPassword"));
const FouroFour = lazy(() => import("./routes/FouroFour"));
const SettingsRedirect = () => {
  const { tab } = useParams();
  return <Navigate to={`/accountsettings/${tab || 'account'}`} replace />;
};

const AppLayout = () => (
  <div>
    <Toaster />
    <Outlet className={"hideScrollBar"} />
  </div>
);

const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<SplashScreen />}>{children}</Suspense>
);

const router = createBrowserRouter([
  {
    element: <AppLayout className={"hideScrollBar"} />,
    errorElement: <RouterErrorPage />,
    children: [
      {
        path: '/',
        element: <Navigate to="/expireds" replace />,
      },
      {
        path: "/signin/",
        element: (
          <SuspenseWrapper>
            <Login />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/forgotpassword",
        element: (
          <SuspenseWrapper>
            <ForgotPassword />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/expireds/*",
        element: (
          <SuspenseWrapper>
            <SearchExpiredsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "settings",
        element: <Navigate to="/accountsettings/account" replace />,
      },
      {
        path: "settings/:tab",
        element: <SettingsRedirect />,
      },
      {
        path: "/oldexpired",
        element: (
          <SuspenseWrapper>
            <OldExpired />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/fsbo",
        element: (
          <SuspenseWrapper>
            <Fsbo />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/preforeclosure",
        element: (
          <SuspenseWrapper>
            <Preforeclosures />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/distressed",
        element: (
          <SuspenseWrapper>
            <Distressed />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/data-genie/*",
        element: (
          <SuspenseWrapper>
            <ImportedListingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/ns/*",
        element: (
          <SuspenseWrapper>
            <NeighborhoodSearchPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/scripts",
        element: (
          <SuspenseWrapper>
            <Scripts />
          </SuspenseWrapper>
        ),
      },
      ...(process.env.REACT_APP_ENABLE_TUTORIALS_PAGE === "true" ? [{
        path: "/tutorials",
        element: (
          <SuspenseWrapper>
            <TutorialsTraining />
          </SuspenseWrapper>
        ),
      }] : []),
      {
        path: "/help",
        element: (
          <SuspenseWrapper>
            <Fsbo />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/accountsettings",
        element: <Navigate to="/accountsettings/account" replace />,
      },
      {
        path: "/accountsettings/:tab",
        element: (
          <SuspenseWrapper>
            <Settings />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/forgotpassword/:token",
        element: (
          <SuspenseWrapper>
            <ResetPassword />
          </SuspenseWrapper>
        ),
      },
      {
        path: "*",
        element: (
          <SuspenseWrapper>
            <FouroFour />
          </SuspenseWrapper>
        ),
      },
    ],
  },
]);

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById("root"));

const App = () => {
  useEffect(() => {
    // Apply OverlayScrollbars to the body
    OverlayScrollbars(document.body, {
      scrollbars: {
        autoHide: "scroll",
      },
    });

    // Clean up on unmount
    return () => {
      OverlayScrollbars(document.body).destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      {/* <React.StrictMode> */}
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
          <SendCardsProvider>
            <AreaProProvider>
              <NavigationProvider>
                <RouterProvider router={router} />
              </NavigationProvider>
            </AreaProProvider>
          </SendCardsProvider>
        </AuthProvider>
      </QueryClientProvider>
      {/* </React.StrictMode> */}
    </ErrorBoundary>
  );
};

root.render(<App />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
